import { motion } from 'motion/react';
import { useEffect, useRef, type ReactNode } from 'react';

import { YStack } from '@/components/ui';
import { useScrollFade } from '@/hooks/useScrollFade';
import { logError } from '@/lib/errorLogging';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import { initializeHomePreferences } from './home-preferences.store';
import { homeSettingsTunnel } from './home-settings-tunnel';
import { HomeSections } from './HomeSections';
import { HomeSettings } from './HomeSettings';
import { useScrollToComposer } from './use-scroll-to-composer';

export type HomeScreenProps = {
  greeting: string;
  canCycleGreeting: boolean;
  greetingDebugLabel?: string;
  onCycleGreeting: () => void;
  isDraggingFile: boolean;
  isFirstRun: boolean;
  composer: ReactNode;
  recommendations: ReactNode;
  banner: ReactNode;
};

export const HomeScreen = ({
  greeting,
  canCycleGreeting,
  greetingDebugLabel,
  onCycleGreeting,
  isDraggingFile,
  isFirstRun,
  composer,
  recommendations,
  banner,
}: HomeScreenProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { maskStyle } = useScrollFade(scrollRef, { fadeSize: 64 });
  useScrollToComposer(scrollRef);

  useEffect(() => {
    trackEvent('HOME_VIEWED');
    initializeHomePreferences().catch((error) => {
      logError(error, 'Failed to initialize home preferences', {
        context: 'HomeScreen:initializeHomePreferences',
      });
    });
  }, []);

  return (
    <YStack
      ref={scrollRef}
      style={maskStyle}
      className="h-full min-w-0 flex-1 overflow-y-auto pt-[var(--panel-header-height)] scrollbar-hide peer-[:not(:empty)]:pt-0"
    >
      <motion.div
        className="flex min-h-full w-full flex-col"
        initial={{ opacity: 0, scale: 0.99 }}
        animate={{ opacity: isDraggingFile ? 0.1 : 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        {!isFirstRun && (
          <homeSettingsTunnel.In>
            <HomeSettings />
          </homeSettingsTunnel.In>
        )}
        <YStack className="home-body w-full flex-1 px-3 pb-10 pt-12 md:px-12 md:pt-16">
          <YStack
            data-home-main
            className="mx-auto w-full max-w-[720px] shrink-0 gap-8"
          >
            <span
              data-home-greeting
              onClick={canCycleGreeting ? onCycleGreeting : undefined}
              title={greetingDebugLabel}
              className={cn(
                'block text-balance text-center font-serif text-2xl tracking-tight text-foreground md:text-3xl',
                canCycleGreeting && 'select-none'
              )}
            >
              {greeting}
            </span>

            <YStack className="gap-3">
              {composer}
              {recommendations}
            </YStack>
            <div data-home-below className="contents">
              {!isFirstRun && <HomeSections />}
              {banner}
            </div>
          </YStack>
        </YStack>
      </motion.div>
    </YStack>
  );
};
